import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { CheckCircle, Users, BarChart, Zap, Shield, Clock, TrendingUp } from 'lucide-react';
import { useParams } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { getBreadcrumbStructuredData, getSoftwareApplicationStructuredData } from '../utils/structuredData';

const ModuleDetails = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const { moduleId } = useParams();
  
  const module = moduleId ? t.solutions.modules[moduleId as keyof typeof t.solutions.modules] : null;

  if (!module) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <SEOHead 
          title="Module Not Found - LAIMU Trantor"
          description="The requested module page was not found."
          noIndex={true}
        />
        <Header />
        <div className="text-center pt-24">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Module not found' : 'Módulo no encontrado'}
          </h1>
          <a href="/" className="text-blue-600 hover:text-blue-800">
            {language === 'en' ? 'Back to Home' : 'Volver al Inicio'}
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  const breadcrumbData = getBreadcrumbStructuredData([
    { name: 'Home', url: 'https://laimu.mx/' },
    { name: 'Solutions', url: 'https://laimu.mx/#solutions' },
    { name: module.title, url: `https://laimu.mx/module/${moduleId}` }
  ]);

  const moduleStructuredData = {
    ...getSoftwareApplicationStructuredData(),
    name: `${module.fullTitle} - LAIMU Trantor`,
    description: module.description,
    featureList: module.features
  };

  const seoTitle = language === 'en' 
    ? `${module.fullTitle} - LAIMU Trantor | ${module.title} Software Solution`
    : `${module.fullTitle} - LAIMU Trantor | Solución de Software ${module.title}`;
    
  const seoDescription = language === 'en'
    ? `Discover LAIMU Trantor's ${module.title} module: ${module.description}. Features include ${module.features.slice(0, 3).join(', ')}. Transform your business operations today.`
    : `Descubre el módulo ${module.title} de LAIMU Trantor: ${module.description}. Las características incluyen ${module.features.slice(0, 3).join(', ')}. Transforma las operaciones de tu negocio hoy.`;

  const moduleKeywords = {
    crm: language === 'en' 
      ? 'CRM software, customer relationship management, sales automation, lead management, customer service, contact management'
      : 'software CRM, gestión relaciones clientes, automatización ventas, gestión leads, servicio cliente, gestión contactos',
    erp: language === 'en'
      ? 'ERP software, enterprise resource planning, business management, inventory management, financial management, supply chain'
      : 'software ERP, planificación recursos empresariales, gestión empresarial, gestión inventario, gestión financiera, cadena suministro',
    tms: language === 'en'
      ? 'TMS software, transportation management, logistics software, fleet management, route optimization, shipping management'
      : 'software TMS, gestión transporte, software logística, gestión flota, optimización rutas, gestión envíos',
    mrp: language === 'en'
      ? 'MRP software, manufacturing resource planning, production planning, inventory control, material requirements planning'
      : 'software MRP, planificación recursos manufactura, planificación producción, control inventario, planificación requerimientos materiales',
    hrms: language === 'en'
      ? 'HRMS software, human resource management, payroll management, employee management, HR automation, workforce management'
      : 'software HRMS, gestión recursos humanos, gestión nómina, gestión empleados, automatización RH, gestión fuerza laboral',
    kpis: language === 'en'
      ? 'KPI dashboard, business intelligence, performance metrics, data analytics, reporting software, business insights'
      : 'dashboard KPI, inteligencia empresarial, métricas rendimiento, analítica datos, software reportes, insights empresariales'
  };

  const moduleIcons = {
    crm: <Users className="w-8 h-8" />,
    erp: <BarChart className="w-8 h-8" />,
    tms: <TrendingUp className="w-8 h-8" />,
    mrp: <Zap className="w-8 h-8" />,
    hrms: <Users className="w-8 h-8" />,
    kpis: <BarChart className="w-8 h-8" />
  };

  const benefits = {
    crm: [
      { 
        title: language === 'en' ? 'Increased Sales' : 'Aumento en Ventas', 
        value: '35%',
        description: language === 'en' ? 'Average sales increase' : 'Aumento promedio en ventas'
      },
      { 
        title: language === 'en' ? 'Customer Retention' : 'Retención de Clientes', 
        value: '90%',
        description: language === 'en' ? 'Customer retention rate' : 'Tasa de retención de clientes'
      },
      { 
        title: language === 'en' ? 'Response Time' : 'Tiempo de Respuesta', 
        value: '2h',
        description: language === 'en' ? 'Average response time' : 'Tiempo promedio de respuesta'
      }
    ],
    erp: [
      { 
        title: language === 'en' ? 'Cost Reduction' : 'Reducción de Costos', 
        value: '25%',
        description: language === 'en' ? 'Operational cost savings' : 'Ahorro en costos operacionales'
      },
      { 
        title: language === 'en' ? 'Process Efficiency' : 'Eficiencia de Procesos', 
        value: '40%',
        description: language === 'en' ? 'Process improvement' : 'Mejora en procesos'
      },
      { 
        title: language === 'en' ? 'Data Accuracy' : 'Precisión de Datos', 
        value: '99%',
        description: language === 'en' ? 'Data accuracy rate' : 'Tasa de precisión de datos'
      }
    ],
    tms: [
      { 
        title: language === 'en' ? 'Route Optimization' : 'Optimización de Rutas', 
        value: '30%',
        description: language === 'en' ? 'Fuel cost savings' : 'Ahorro en costos de combustible'
      },
      { 
        title: language === 'en' ? 'Delivery Time' : 'Tiempo de Entrega', 
        value: '45%',
        description: language === 'en' ? 'Faster deliveries' : 'Entregas más rápidas'
      },
      { 
        title: language === 'en' ? 'Fleet Utilization' : 'Utilización de Flota', 
        value: '85%',
        description: language === 'en' ? 'Fleet efficiency' : 'Eficiencia de flota'
      }
    ],
    mrp: [
      { 
        title: language === 'en' ? 'Production Efficiency' : 'Eficiencia de Producción', 
        value: '50%',
        description: language === 'en' ? 'Production improvement' : 'Mejora en producción'
      },
      { 
        title: language === 'en' ? 'Waste Reduction' : 'Reducción de Desperdicios', 
        value: '35%',
        description: language === 'en' ? 'Material waste reduction' : 'Reducción de desperdicios de material'
      },
      { 
        title: language === 'en' ? 'On-time Delivery' : 'Entrega a Tiempo', 
        value: '95%',
        description: language === 'en' ? 'On-time delivery rate' : 'Tasa de entrega a tiempo'
      }
    ],
    hrms: [
      { 
        title: language === 'en' ? 'Employee Satisfaction' : 'Satisfacción del Empleado', 
        value: '85%',
        description: language === 'en' ? 'Employee satisfaction rate' : 'Tasa de satisfacción del empleado'
      },
      { 
        title: language === 'en' ? 'HR Process Time' : 'Tiempo de Procesos RH', 
        value: '60%',
        description: language === 'en' ? 'Faster HR processes' : 'Procesos RH más rápidos'
      },
      { 
        title: language === 'en' ? 'Compliance Rate' : 'Tasa de Cumplimiento', 
        value: '100%',
        description: language === 'en' ? 'Regulatory compliance' : 'Cumplimiento regulatorio'
      }
    ],
    kpis: [
      { 
        title: language === 'en' ? 'Decision Speed' : 'Velocidad de Decisión', 
        value: '70%',
        description: language === 'en' ? 'Faster decision making' : 'Toma de decisiones más rápida'
      },
      { 
        title: language === 'en' ? 'Data Visibility' : 'Visibilidad de Datos', 
        value: '100%',
        description: language === 'en' ? 'Real-time visibility' : 'Visibilidad en tiempo real'
      },
      { 
        title: language === 'en' ? 'ROI Tracking' : 'Seguimiento de ROI', 
        value: '95%',
        description: language === 'en' ? 'ROI measurement accuracy' : 'Precisión de medición de ROI'
      }
    ]
  };

  const currentBenefits = benefits[moduleId as keyof typeof benefits] || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={seoTitle}
        description={seoDescription}
        url={`/module/${moduleId}`}
        structuredData={[breadcrumbData, moduleStructuredData]}
        keywords={moduleKeywords[moduleId as keyof typeof moduleKeywords]}
        image={module.imageSrc}
      />
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            {/* Hero Section */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-12">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="p-8 md:p-12">
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#e0f2fe' }}>
                      <span style={{ color: '#115d7b' }}>
                        {moduleIcons[moduleId as keyof typeof moduleIcons]}
                      </span>
                    </div>
                    <div>
                      <span className="inline-block px-3 py-1 rounded-full text-sm font-medium mb-2" style={{ backgroundColor: '#e0f2fe', color: '#115d7b' }}>
                        {module.title}
                      </span>
                      <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                        {module.fullTitle}
                      </h1>
                    </div>
                  </div>
                  <p className="text-xl text-gray-600 mb-8">
                    {module.description}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="/demo"
                      className="px-6 py-3 text-white rounded-md transition-colors font-medium text-center"
                      style={{ backgroundColor: '#115d7b' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
                    >
                      {language === 'en' ? 'Request Demo' : 'Solicitar Demo'}
                    </a>
                    <a
                      href="/#contact"
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium text-center"
                    >
                      {language === 'en' ? 'Contact Sales' : 'Contactar Ventas'}
                    </a>
                  </div>
                </div>
                <div className="relative overflow-hidden flex items-center justify-center bg-gray-50 border border-gray-200 rounded-xl p-4 min-h-[400px] shadow-inner">
                  <img 
                    src={module.imageSrc}
                    alt={`${module.title} software interface`}
                    className="max-w-full max-h-[380px] object-contain rounded-lg shadow-md"
                    loading="eager"
                  />
                </div>
              </div>
            </div>

            {/* Features Section */}
            <div className="grid md:grid-cols-2 gap-12 mb-12">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {language === 'en' ? 'Key Features' : 'Características Principales'}
                </h2>
                <div className="space-y-4">
                  {module.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle className="w-6 h-6 mr-3 mt-1 flex-shrink-0" style={{ color: '#10b981' }} />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {language === 'en' ? 'Benefits & Results' : 'Beneficios y Resultados'}
                </h2>
                <div className="space-y-6">
                  {currentBenefits.map((benefit, index) => (
                    <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-900">{benefit.title}</h3>
                        <span className="text-3xl font-bold" style={{ color: '#115d7b' }}>
                          {benefit.value}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm">{benefit.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Integration Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                {language === 'en' ? 'Seamless Integration' : 'Integración Perfecta'}
              </h2>
              <p className="text-gray-600 text-center mb-8 max-w-3xl mx-auto">
                {language === 'en' 
                  ? 'Our modules work together seamlessly, sharing data and insights across your entire organization for maximum efficiency and visibility.'
                  : 'Nuestros módulos trabajan juntos sin problemas, compartiendo datos e información en toda tu organización para máxima eficiencia y visibilidad.'
                }
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {Object.entries(t.solutions.modules).map(([key, mod]) => (
                  <a
                    key={key}
                    href={`/module/${key}`}
                    className={`p-4 rounded-lg text-center transition-all hover:shadow-lg transform hover:-translate-y-1 ${
                      key === moduleId 
                        ? 'shadow-md border-2' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    style={key === moduleId ? { borderColor: '#115d7b', backgroundColor: '#e0f2fe' } : {}}
                  >
                    <div className="w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center" style={{ backgroundColor: key === moduleId ? '#115d7b' : '#e0f2fe' }}>
                      <span style={{ color: key === moduleId ? 'white' : '#115d7b' }}>
                        {moduleIcons[key as keyof typeof moduleIcons]}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{mod.title}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center">
              <div className="bg-white p-8 rounded-xl shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {language === 'en' ? 'Ready to Get Started?' : '¿Listo para Comenzar?'}
                </h2>
                <p className="text-gray-600 mb-6">
                  {language === 'en' 
                    ? 'See how this module can transform your business operations.'
                    : 'Ve cómo este módulo puede transformar las operaciones de tu negocio.'
                  }
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/demo"
                    className="px-6 py-3 text-white rounded-md transition-colors font-medium"
                    style={{ backgroundColor: '#115d7b' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
                  >
                    {language === 'en' ? 'Schedule Demo' : 'Agendar Demo'}
                  </a>
                  <a
                    href="/#contact"
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
                  >
                    {language === 'en' ? 'Contact Expert' : 'Contactar Experto'}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ModuleDetails;